import React, { useEffect } from 'react';
import { Card, LinearProgress } from '@material-ui/core';
import { Notes as NotesIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUserContext } from '../../../user/User.context';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useUpdateFile } from '../Document.hook';
import {
  BaseDocumentCardProps,
  CreateFileVersionDto
} from '../Document.interface';

type DocumentReasonForChangeForm = Pick<
  CreateFileVersionDto,
  'reasonForChange'
>;

const DocumentReasonForChangeCard: React.FC<BaseDocumentCardProps> = props => {
  const { document, onChange, onUpdate, onError } = props;
  const can = useUserContext().permissions.file;
  const [
    { data: updatedFile, isLoading: isUpdating, error: updateError },
    updateDocument
  ] = useUpdateFile(document.id, document.isFolder);
  const {
    updatedTask,
    isUpdating: isUpdatingTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(document.task?.id || 0);
  const isLoading: boolean = isUpdating || isUpdatingTask;
  const error: FetchError | undefined = updateError || updateTaskError;

  const initialValues: DocumentReasonForChangeForm = {
    reasonForChange:
      document.fileVersion?.reasonForChange || document.reasonForChange || ''
  };

  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.File>(
    document.task
  );

  useEffect(() => {
    if (updatedFile) {
      onChange?.(updatedFile);
      onUpdate?.();
    }
  }, [updatedFile]);

  useEffect(() => {
    if (updatedTask) {
      document.task = Object.assign(document.task || {}, updatedTask);
      onChange?.(document);
      onUpdate?.();
    }
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
  }, [error]);
  return (
    <Card>
      {isLoading && <LinearProgress />}
      <CardTitle>
        <NotesIcon />
        Reason For Change
      </CardTitle>
      <CardContent>
        <InlineFormik
          isSubmitting={isLoading}
          initialValues={initialValues}
          onSubmit={(values: DocumentReasonForChangeForm) => {
            updateDocument({ ...values });
          }}
        >
          <InlineHtml
            name='reasonForChange'
            disabled={
              isLoading ||
              can.not.update ||
              Boolean(document.fileVersion?.reasonForChange) ||
              isFieldDisabled('reasonForChange')
            }
            attachments={document.task?.attachments || []}
            onChangeAttachments={(attachments: InlineFileValue[]) => {
              updateTask({ attachments });
              if (document.task) {
                document.task.attachments = attachments;
              }
              onChange?.(document);
            }}
          />
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default DocumentReasonForChangeCard;
